import { useState, useEffect, useCallback } from 'react';
import { alertService } from '../services/alertService';

/**
 * Hook for managing alerts
 */
export const useAlerts = (options = {}) => {
  const {
    userId = null,
    severity = null,
    activeOnly = true,
    autoRefresh = false,
    refreshInterval = 30000 // 30 seconds
  } = options;
  
  const [alerts, setAlerts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [counts, setCounts] = useState({
    critical: 0,
    high: 0,
    medium: 0,
    total: 0,
    unread: 0
  });
  
  const fetchAlerts = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      
      let fetchedAlerts;
      
      if (userId) {
        if (severity) {
          // Get by user and filter by severity client-side
          const userAlerts = await alertService.getByUser(userId);
          fetchedAlerts = userAlerts.filter(a => a.severity === severity);
        } else if (activeOnly) {
          // Get all user alerts and filter active
          const userAlerts = await alertService.getByUser(userId);
          fetchedAlerts = userAlerts.filter(a => a.status === 'ACTIVE');
        } else {
          fetchedAlerts = await alertService.getByUser(userId);
        }
        
        // Also fetch counts
        const alertCounts = await alertService.getCountBySeverity(userId);
        setCounts(alertCounts);
      } else if (severity) {
        fetchedAlerts = await alertService.getBySeverity(severity);
      } else if (activeOnly) {
        fetchedAlerts = await alertService.getActive();
      } else {
        fetchedAlerts = await alertService.getAll();
      }
      
      setAlerts(fetchedAlerts);
    } catch (err) {
      console.error('Error fetching alerts:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [userId, severity, activeOnly]);
  
  useEffect(() => {
    fetchAlerts();
    
    // Set up auto-refresh if enabled
    let intervalId;
    if (autoRefresh) {
      intervalId = setInterval(fetchAlerts, refreshInterval);
    }
    
    return () => {
      if (intervalId) {
        clearInterval(intervalId);
      }
    };
  }, [fetchAlerts, autoRefresh, refreshInterval]);
  
  const markAsRead = useCallback(async (alertId) => {
    if (!userId) {
      console.warn('Cannot mark as read without userId');
      return false;
    }
    
    try {
      await alertService.markAsRead(alertId, userId);
      // Update local state
      setAlerts(prev => prev.map(alert => 
        alert.id === alertId ? { ...alert, read: true } : alert
      ));
      // Update counts
      setCounts(prev => ({
        ...prev,
        unread: Math.max(0, prev.unread - 1)
      }));
      return true;
    } catch (error) {
      console.error('Error marking alert as read:', error);
      return false;
    }
  }, [userId]);
  
  const acknowledge = useCallback(async (alertId, userName) => {
    if (!userId) {
      console.warn('Cannot acknowledge without userId');
      return false;
    }
    
    try {
      await alertService.acknowledge(alertId, userId, userName);
      // Refresh alerts to get updated acknowledgment
      await fetchAlerts();
      return true;
    } catch (error) {
      console.error('Error acknowledging alert:', error);
      return false;
    }
  }, [userId, fetchAlerts]);
  
  const dismiss = useCallback(async (alertId, reason = '') => {
    if (!userId) {
      console.warn('Cannot dismiss without userId');
      return false;
    }
    
    try {
      await alertService.dismiss(alertId, userId, reason);
      // Remove from local state
      setAlerts(prev => prev.filter(alert => alert.id !== alertId));
      // Update counts
      setCounts(prev => ({
        ...prev,
        total: Math.max(0, prev.total - 1)
      }));
      return true;
    } catch (error) {
      console.error('Error dismissing alert:', error);
      return false;
    }
  }, [userId]);
  
  const resolve = useCallback(async (alertId, resolution = '') => {
    if (!userId) {
      console.warn('Cannot resolve without userId');
      return false;
    }
    
    try {
      await alertService.resolve(alertId, userId, resolution);
      // Remove from active alerts
      setAlerts(prev => prev.filter(alert => alert.id !== alertId));
      // Update counts
      setCounts(prev => ({
        ...prev,
        total: Math.max(0, prev.total - 1)
      }));
      return true;
    } catch (error) {
      console.error('Error resolving alert:', error);
      return false;
    }
  }, [userId]);
  
  const refresh = useCallback(() => {
    fetchAlerts();
  }, [fetchAlerts]);
  
  return {
    alerts,
    loading,
    error,
    counts,
    markAsRead,
    acknowledge,
    dismiss,
    resolve,
    refresh
  };
};

/**
 * Hook for getting unread alert count (for badges)
 */
export const useUnreadAlertCount = (userId) => {
  const [count, setCount] = useState(0);
  const [loading, setLoading] = useState(true);
  
  useEffect(() => {
    if (!userId) {
      setCount(0);
      setLoading(false);
      return;
    }
    
    const fetchCount = async () => {
      try {
        const unreadAlerts = await alertService.getUnread(userId);
        setCount(unreadAlerts.length);
      } catch (error) {
        console.error('Error fetching unread count:', error);
        setCount(0);
      } finally {
        setLoading(false);
      }
    };
    
    fetchCount();
    
    // Refresh every minute
    const intervalId = setInterval(fetchCount, 60000);
    
    return () => clearInterval(intervalId);
  }, [userId]);
  
  return { count, loading };
};

export default useAlerts;


